import { LinkIcon } from '@heroicons/react/20/solid';

import { UserShipmentInfo } from 'src/shared/types/bulkOrdering';

import ShipmentFileLinks from 'src/shared/components/ShipmentFileLinks';
import { Chip, Divider } from 'src/shared/components/ui';
import { Typography } from 'src/shared/components/ui';

const SHIPMENT_STATUS_LABELS: Record<string, string> = {
  delivered: 'Delivered',
  in_transit: 'In Transit',
  out_for_delivery: 'Out for Delivery',
  available_for_pickup: 'Available for Pickup',
  prepared_to_ship: 'Prepared to Ship',
  exception: 'Exception'
};

interface OrderShipmentsTabProps {
  shipments: UserShipmentInfo[];
}

const OrderShipmentsTab: React.FC<OrderShipmentsTabProps> = ({ shipments }) => {
  if (shipments.length === 0) {
    return (
      <div className="px-5 pb-7">
        <Typography variant="body-1" className="text-secondary-text">
          No shipments
        </Typography>
      </div>
    );
  }

  return (
    <div className="px-5 flex flex-col gap-4 pb-7">
      {shipments.map((shipment, index) => (
        <div key={shipment.id}>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Typography variant="body-1" weight="bold">
                Shipment {index + 1}
              </Typography>
              <Chip color="default" className="bg-gray-2 rounded-lg">
                <Typography
                  variant="body-3"
                  weight="medium"
                  className="text-secondary-text"
                >
                  {SHIPMENT_STATUS_LABELS[shipment.status] ?? shipment.status}
                </Typography>
              </Chip>
            </div>

            {(shipment.tracking_number || shipment.tracking_url) && (
              <div className="flex items-center gap-2">
                <Typography
                  variant="body-2"
                  className="text-secondary-text"
                  weight="medium"
                >
                  Tracking:
                </Typography>
                <div
                  onClick={() => {
                    if (shipment.tracking_url) {
                      window.open(shipment.tracking_url, '_blank');
                    }
                  }}
                  className={
                    shipment.tracking_url
                      ? 'flex items-center gap-1 cursor-pointer hover:opacity-50 transition-opacity'
                      : 'flex items-center gap-1'
                  }
                >
                  <LinkIcon className="size-4 pointer-events-none text-primary" />
                  <Typography className="text-primary text-sm">
                    {shipment.tracking_number || 'Tracking link'}
                  </Typography>
                </div>
              </div>
            )}

            {shipment.submission_client_notes && (
              <div className="flex flex-col gap-1">
                <Typography
                  variant="body-2"
                  className="text-secondary-text"
                  weight="medium"
                >
                  Submission Notes:
                </Typography>
                <Typography variant="body-2">
                  {shipment.submission_client_notes}
                </Typography>
              </div>
            )}

            {shipment.reception_client_notes && (
              <div className="flex flex-col gap-1">
                <Typography
                  variant="body-2"
                  className="text-secondary-text"
                  weight="medium"
                >
                  Reception Notes:
                </Typography>
                <Typography variant="body-2">
                  {shipment.reception_client_notes}
                </Typography>
              </div>
            )}

            {shipment.external_files.length > 0 && (
              <div className="flex flex-col gap-1">
                <Typography
                  variant="body-2"
                  className="text-secondary-text"
                  weight="medium"
                >
                  Files:
                </Typography>
                <ShipmentFileLinks files={shipment.external_files} />
              </div>
            )}
          </div>

          {index < shipments.length - 1 && <Divider className="mt-4" />}
        </div>
      ))}
    </div>
  );
};

export default OrderShipmentsTab;
